import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { Client, Versions } from "@stomp/stompjs";
import { PollResponse } from "abweb/dto/async/PollResponse";
import { RemoteAbInitioErrorException } from "abweb/dto/exception/RemoteAbInitioErrorException";
import { RemoteError } from "abweb/dto/exception/RemoteError";
import * as AbWebResources from "abweb/generated/locale/AbWebResources";
import { AbWebServices } from "abweb/service/AbWebServices";
import { AsyncTask } from "abweb/service/rpc/AsyncTask";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { IdleMonitor } from "abweb/utils/IdleMonitor";
import { DeserializeContext } from "abweb/utils/json/DeserializeContext";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { URLUtils } from "abweb/utils/URLUtils";
import { HttpHeartbeat } from "./HttpHeartbeat";
const LOG = LogUtils.getLogger(__filename);
let TASK_POLLER_INSTANCE_COUNTER = 0;
export class TaskPoller {
  // define iff using directProxy (same origin),  e.g. '/../datacatalog'

  constructor(pollingService, idleTimeout, sessionTimeout, isWSDisabled, rpcService = AbWebServices.getRPCService(), proxyRoot = "") {
    _defineProperty(this, "taskPollerId", ++TASK_POLLER_INSTANCE_COUNTER);
    _defineProperty(this, "nextTaskId", 1);
    _defineProperty(this, "pollingService", void 0);
    _defineProperty(this, "activePolls", new Map());
    _defineProperty(this, "httpHeartbeat", void 0);
    /** The number of pending results reported by the server in the last poll */
    _defineProperty(this, "pendingResults", 0);
    /** Indicates we have initiated a polling request, the next pendingResult received from server may not account for it */
    _defineProperty(this, "pollRequested", false);
    /** Flag indicating our poll should be simple 30 second ping instead of a long poll */
    _defineProperty(this, "pingOnly", false);
    /** Flag indicating polling service has been cancelled */
    _defineProperty(this, "pollingCanceled", void 0);
    // @ts-ignore: noUnusedLocals
    _defineProperty(this, "sessionTimeout", void 0);
    _defineProperty(this, "idleTimeout", void 0);
    _defineProperty(this, "isIdle", false);
    _defineProperty(this, "rpcService", void 0);
    /** Represents whether stompClient is connected */
    _defineProperty(this, "isConnectedToWS", false);
    /** isHandShakeComplete = true iff isConnectedToWS and handshook successfully */
    _defineProperty(this, "isHandshakeComplete", false);
    /** Flag assigned in PollConfiguration.java to disable WebSockets */
    _defineProperty(this, "isWSDisabled", void 0);
    /**
     * Value of isWSDisabled passed into the constructor, and is used within TaskPoller.reset()
     * to create new TaskPoller instance. Note previous session may have fallen back to isWSDisabled=true (aka long polling)
     * due to lost session related 403 error, but on new TaskPoller want to again
     * try to use websockets (assuming isWSDisabledOriginal = false)
     */
    _defineProperty(this, "isWSDisabledOriginal", void 0);
    /** Guard for establishWS() */
    _defineProperty(this, "isEstablishingWS", void 0);
    _defineProperty(this, "idleMonitorHandle", null);
    _defineProperty(this, "proxyRoot", void 0);
    /** Callback called when connection over WS was successful. */
    _defineProperty(this, "onConnect", frame => {
      LOG.info(`WebSocket onConnect frame: ${frame.body}`);
      let clientSessionId = this.rpcService.clientSessionId;
      this.stompClient.subscribe("/user/" + clientSessionId + "/rpc-response", this.rpcCallback);
      let username = AbWebServices.sessionData.currentUser.username;
      this.stompClient.subscribe("/user/" + username + "/handshake", this.handshakeCallback);
      this.isConnectedToWS = true;
      this.onWSConnect();
    });
    _defineProperty(this, "onWSClose", evt => {
      LOG.info(`WebSocket closed`);
      this.teardownWebsocket();
      if (!this.isEstablishingWS) {
        LOG.info("Reattempting streaming connection");
        this.poll(); // WebSocket connection closed, possibly by a server session timeout. Try to re-establish a streaming connection.
      }
    });
    /** Attempts to connect to WebSocket */
    _defineProperty(this, "connect", () => {
      if (!this.stompClient) {
        this.stompClient = new Client();
        this.stompClient.stompVersions = new Versions(['1.2']); // 1.1 required for heartbeat, some server upgrade strategies cannot handle more than one option
        this.stompClient.onConnect = this.onConnect;
        this.stompClient.onWebSocketError = this.onWSError;
        this.stompClient.onWebSocketClose = this.onWSClose;
        const clientSessionId = this.rpcService.clientSessionId;
        const defaultBrokerUrl = "ws" + URLUtils.getAbsoluteUrl(this.proxyRoot + "/api/abweb/absdk-websocket").substring(4) + "?clientId=" + clientSessionId; //Replaces "http" with "ws" and appends the client session ID (so either ws: or wss:)

        this.stompClient.brokerURL = this.webSocketURLOverrideForTest ?? defaultBrokerUrl;
        if (this.webSocketURLOverrideForTest) {
          LOG.info("Default Broker URL:" + defaultBrokerUrl);
          LOG.info("webSocketOverrideForTest: " + this.webSocketURLOverrideForTest);
        }
        this.stompClient.reconnectDelay = 0; //disables auto-reconnect
      }
      this.stompClient.activate();
    });
    /** Called when receiving a handshake */
    _defineProperty(this, "handshakeCallback", greeting => {
      this.isHandshakeComplete = true;
      LOG.info("WebSocket handshake successful");
      if (!this.onHandshake) {
        // connection timeout happens in first Promise in establishWS()
        LOG.warn("WebSocket handshake aborted due to connection timeout.");
        return;
      }
      this.onHandshake();
    });
    /** Called when receiving an RPC response over WS */
    _defineProperty(this, "rpcCallback", greeting => {
      let pollResponse = new PollResponse();
      pollResponse.initFromJson(JSON.parse(greeting.body), new DeserializeContext());
      this.missedMessageGuard(pollResponse);
      this.processPollResponse(pollResponse);
      this.pendingResults = pollResponse.numPending;
      if (this.pendingResults === 0 && this.isIdle) {
        this.httpHeartbeat.stopPinging();
      }
    });
    /** STOMP.js callback for when an error over WS occurs */
    _defineProperty(this, "onWSError", evt => {
      LOG.warn(`Websocket error: ${JSON.stringify(evt)}`);
      this.onWSFail(new Error("WebSocket error of type " + evt.type));
    });
    /** Sends the handshake to the server */
    _defineProperty(this, "submit", () => {
      this.stompClient.publish({
        destination: "/app/handshake",
        body: JSON.stringify({
          "name": "handshake"
        })
      });
    });
    this.isConnectedToWS = false;
    this.pollingService = pollingService;
    this.sessionTimeout = sessionTimeout;
    this.idleTimeout = idleTimeout;
    this.pollingCanceled = false;
    this.rpcService = rpcService;
    this.isWSDisabledOriginal = this.isWSDisabled = isWSDisabled;
    this.isEstablishingWS = false;
    this.proxyRoot = proxyRoot;
    this.httpHeartbeat = new HttpHeartbeat(rpcService); // could be proxied rpc service, so pass in

    if (this.idleTimeout > 0) {
      this.idleMonitorHandle = IdleMonitor.addListener(this.idleTimeout, () => {
        LOG.info("Keep-Alive timeout");
        this.isIdle = true;
        if (this.pendingResults === 0) {
          this.stopPolling();
        }
      }, () => {
        this.isIdle = false;
        this.startPolling();
      });
    } else if (this.idleTimeout === 0) {
      this.isIdle = true;
    }
    if (this.idleTimeout !== 0) {
      this.startPolling();
    }
  }
  setWebSocketURL(webSocketURL) {
    let clientSessionId = this.rpcService.clientSessionId;
    this.webSocketURLOverrideForTest = webSocketURL + "/api/abweb/absdk-websocket?clientId=" + clientSessionId;
    if (this.stompClient != null) {
      this.stompClient.brokerURL = this.webSocketURLOverrideForTest;
    }
  }
  reset() {
    LOG.debug("Resetting TaskPoller");
    this.stopPolling();
    // Note all args are readonly members, so get 'fresh' new instance equivalent of current taskPoller instance
    return new TaskPoller(this.pollingService, this.idleTimeout, this.sessionTimeout, this.isWSDisabledOriginal);
  }
  /** Connects to WS and then attempts a handshake */
  async establishWS() {
    this.isEstablishingWS = true;
    if (this.isWSDisabled) {
      LOG.info("WebSocket disabled");
      return;
    }
    try {
      await new Promise((connectResolve, connectReject) => {
        this.onWSConnect = connectResolve;
        this.onWSFail = connectReject;
        if (!this.isConnectedToWS) {
          this.connect();
          setTimeout(() => {
            if (!this.isConnectedToWS) {
              this.onWSFail(new Error("WebSocket connect timed out"));
              LOG.warn("WebSocket connect timed out");
            }
          }, 5000);
        }
      });
      await new Promise((handshakeResolve, handshakeReject) => {
        this.onHandshake = handshakeResolve;
        this.submit();
        setTimeout(() => {
          if (!this.isHandshakeComplete) {
            handshakeReject(new Error("WebSocket handshake timed out"));
            LOG.warn("WebSocket handshake timed out");
          }
        }, 5000);
      });
      // Cause server to flush any existing long poll and switch to websockets
      LOG.debug("Switching to websockets");
      await this.rpcService.invokeVoid("/api/abweb/polling/ws", null);
      this.httpHeartbeat.startPinging();
    } catch (err) {
      LOG.warn(`WS Error: ${err}`);
      this.isWSDisabled = true;
    }
    this.isEstablishingWS = false;
  }
  startPollingTask(appRelativeUrl, namedParams, pollHandler, init) {
    const taskId = String(++this.nextTaskId);
    appRelativeUrl = this.addPollingParams(appRelativeUrl, taskId);
    const task = new AsyncTask(taskId, appRelativeUrl, this.pollingService.startTask(appRelativeUrl, namedParams, init), launchResponse => new Promise((resolve, reject) => {
      pollHandler.handleLaunchResponse(task, launchResponse, resolve, reject);
      this.checkCanceled(task);
      this.startPolling();
    }), this);
    this.activePolls.set(taskId, pollHandler);
    this.startPolling();
    return task;
  }
  startPollingTaskVoid(appRelativeUrl, namedParams, pollHandler, init) {
    const taskId = String(++this.nextTaskId);
    appRelativeUrl = this.addPollingParams(appRelativeUrl, taskId);
    const task = new AsyncTask(taskId, appRelativeUrl, this.pollingService.startTask(appRelativeUrl, namedParams, init), launchResponse => new Promise((resolve, reject) => {
      pollHandler.handleLaunchResponse(task, launchResponse, resolve, reject);
      this.checkCanceled(task);
      this.startPolling();
    }), this);
    this.activePolls.set(taskId, pollHandler);
    this.startPolling();
    return task;
  }
  addPollingParams(appRelativeUrl, taskId) {
    appRelativeUrl += appRelativeUrl.indexOf("?") === -1 ? "?polling=true" : "&polling=true";
    appRelativeUrl += "&" + TaskPoller.TASK_ID + "=" + taskId;
    return appRelativeUrl;
  }
  checkCanceled(task) {
    if (task.canceled) {
      LOG.warn(`Task [${task.taskId}] for [${task.url}] cancelled by client while request was still pending.`);
      task.fulfillCancel(id => {
        return this.pollingService.cancelTask(id);
      });
    }
  }
  cancel(taskId) {
    const pollHandler = this.activePolls.get(taskId);
    if (pollHandler) {
      return this.pollingService.cancelTask(taskId);
    } else {
      LOG.warn(`Cancel for unknown task [${taskId}]`);
      return null;
    }
  }
  startPolling() {
    this.pollRequested = true;
    this.pingOnly = false;
    this.poll();
  }
  stopPolling() {
    if (this.idleMonitorHandle !== null) {
      IdleMonitor.removeListener(this.idleMonitorHandle);
    }
    this.pollingCanceled = true;
    this.teardownWebsocket();
  }
  get canceled() {
    return this.pollingCanceled;
  }
  teardownWebsocket() {
    if (this.isConnectedToWS) {
      LOG.info("tearing down WebSocket");
      this.httpHeartbeat.stopPinging();
      this.stompClient.deactivate();
      this.isConnectedToWS = false;
      this.isHandshakeComplete = false;
    }
  }
  async poll() {
    LOG.debug("calling poll");
    if (!this.shouldPoll()) {
      return;
    }

    //Attempt to establish a WebSocket connection. If it succeeds, return.
    if (!this.isWSDisabled && !this.isHandshakeComplete && !this.isEstablishingWS) {
      await this.establishWS();
    }
    if (!this.isWSDisabled) {
      LOG.debug("WS not disabled - will startPinging");
      this.httpHeartbeat.startPinging();
      return;
    }

    // No ws polling established, so use long polling
    try {
      LOG.debug("Starting a new poll");
      this.pollRequested = false;
      this.pollPending = true;
      return this.pollingService.poll(this.pingOnly).then(pollResponse => {
        this.missedMessageGuard(pollResponse);
        this.pollPending = false;
        LOG.debug("Got poll response");
        if (pollResponse.shutdown) {
          LOG.warn("Server shutting down poll");
          this.stopPolling();
          return;
        }
        this.pendingResults = pollResponse.numPending;
        this.pingOnly = this.pendingResults === 0 && !this.pollRequested && !this.isIdle;
        this.pollingService.setPollTimeout(() => {
          this.poll();
        }, this.pingOnly ? 30 * 1000 : 0);
        if (pollResponse) {
          setTimeout(() => {
            this.processPollResponse(pollResponse);
          }, 0);
        }
      }).catch(error => {
        this.pollPending = false;
        this.pollingService.setPollTimeout(() => {
          this.startPolling();
        }, 30 * 1000);
        if (ClassUtils.isInstanceOf(error, RemoteError)) {
          if (ClassUtils.isInstanceOf(error.remoteException, RemoteAbInitioErrorException)) {
            if (error.remoteException.messageCode === "rpc.missedMessages") {
              LOG.warn("Poll responding with rpc.missedMessages error, re-throwing");
              throw error;
            }
          }
        }
        LOG.warn("Swallowing error from poll: " + error.toString());
      });
    } catch (error) {
      this.pollPending = false;
      this.pollingService.setPollTimeout(() => {
        this.startPolling();
      }, 30 * 1000);
      LOG.warn("Error running poll: " + error);
    }
  }
  shouldPoll() {
    LOG.debug("calling shouldPoll");
    if (!this.isWSDisabled && this.isHandshakeComplete) {
      LOG.debug("WebSocket already connected");
      return false; //Disable polling if using WebSockets
    }
    if (this.pollPending) {
      LOG.debug("Not starting new poll because one is already in flight.");
      return false;
    }
    if (this.pollingCanceled) {
      LOG.info("Not starting new poll because polling has been cancelled.");
      return false;
    }
    if (this.isIdle && this.pendingResults === 0 && !this.pollRequested) {
      LOG.info("Not starting new poll because the application has gone idle and there are no pending poll responses or requests.");
      return false;
    }
    return true;
  }
  processPollResponse(pollResponse) {
    let responses = pollResponse.responses;
    if (responses == null) {
      return;
    }
    for (const response of responses) {
      if (!response) {
        continue;
      }
      if (response.taskId == null) {
        LOG.warn("Missing task id for poll response.");
        continue;
      }
      const pollHandler = this.activePolls.get(response.taskId);
      if (!pollHandler) {
        LOG.warn(`Unknown task id [${response.taskId}]`);
      } else {
        setTimeout(() => {
          this.handlePollResponse(pollHandler, response);
        }, 0);
      }
    }
  }
  handlePollResponse(pollHandler, response) {
    if (pollHandler.handlePollResponse(response)) {
      this.activePolls.delete(response.taskId);
    }
  }
  missedMessageGuard(pollResponse) {
    const newResponseStamp = Number(pollResponse.responseStamp);
    if (this.responseStamp != null) {
      const expectedResponseStamp = pollResponse.responses.length > 0 ? this.responseStamp + 1 : this.responseStamp;
      if (newResponseStamp > expectedResponseStamp) {
        LOG.error("Missed message with response stamp: " + expectedResponseStamp);
        // this format of RemoteAbInitioErrorException inside RemoteError is to display correct error dialog
        throw new RemoteError(AbWebResources.rpc_missedMessages(), null, null, RemoteAbInitioErrorException.createRemoteAbInitioErrorException(null, AbWebResources.rpc_missedMessages(), null, "rpc.missedMessages", null, null, null));
      }
    }
    this.responseStamp = Number(pollResponse.responseStamp);
  }
}
_defineProperty(TaskPoller, "TASK_ID", "abTaskId");
//# sourceMappingURL=TaskPoller.js.map
